<script setup lang="ts">
import type { ClinicResponse, NearestClinicResponse } from '@/types'
import { isCurrentlyOpen } from '@/types'

/** Fields ClinicCard actually renders. Both ClinicResponse and NearestClinicResponse satisfy this. */
export interface ClinicCardClinic {
  id: string
  slug: string
  name: string
  phone: string | null
  fullAddress: string | null
  isEmergency: boolean
  is24Hours: boolean
  isOnCallOnly: boolean
  rating: number | null
  reviewCount: number
  workingHours: ClinicResponse['workingHours']
  emergencyHours: ClinicResponse['emergencyHours']
  emergencyPhone: string | null
  isVerified: boolean
  isPremium: boolean
  about?: string | null
  imageUrl?: string | null
  specializations?: string[]
  city: Omit<ClinicResponse['city'], 'id'> & { id?: string }
}

const props = defineProps<{
  clinic: ClinicCardClinic & Partial<Pick<NearestClinicResponse, 'distanceKm'>>
  showDistance?: boolean
}>()

const clinicUrl = computed(() => {
  return `/${props.clinic.city.county.slug}/${props.clinic.city.slug}/${props.clinic.slug}`
})

const distance = computed(() => {
  if ('distanceKm' in props.clinic) {
    return props.clinic.distanceKm
  }
  return null
})

// Emergency status for smart phone routing
const { isEmergencyActive } = useEmergencyStatus(
  computed(() => props.clinic.workingHours),
  computed(() => props.clinic.isEmergency ? props.clinic.emergencyHours : null),
  computed(() => props.clinic.is24Hours),
)

// Client-side open status for visual styling
const isMounted = ref(false)
onMounted(() => {
  isMounted.value = true
})

const cardStatus = computed<'open' | 'emergency' | 'closed' | 'unknown'>(() => {
  if (!isMounted.value) return 'unknown'
  if (props.clinic.is24Hours) return 'open'
  if (props.clinic.workingHours && isCurrentlyOpen(props.clinic.workingHours) === true) return 'open'
  if (isEmergencyActive.value) return 'emergency'
  return 'closed'
})

const borderClass = computed(() => {
  if (cardStatus.value === 'open') return 'border-l-4 border-l-emerald-500'
  if (cardStatus.value === 'emergency') return 'border-l-4 border-l-amber-500'
  return ''
})

// Analytics tracking for premium card buttons
const { trackPhoneClick, trackDirectionsClick } = useClinicAnalytics()

const directionsUrl = computed(() => {
  const address = props.clinic.fullAddress || `${props.clinic.city.name}, ${props.clinic.city.county.name}`
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
})

function handleCall(event: Event) {
  event.preventDefault()
  event.stopPropagation()
  if (import.meta.client) {
    const emergencyPh = isEmergencyActive.value && props.clinic.emergencyPhone
    const phone = emergencyPh || props.clinic.phone
    if (phone) {
      trackPhoneClick(props.clinic.id)
      window.location.href = `tel:${phone.replace(/\s/g, '')}`
    }
  }
}

function handleDirections(event: Event) {
  event.preventDefault()
  event.stopPropagation()
  if (import.meta.client) {
    trackDirectionsClick(props.clinic.id)
    window.open(directionsUrl.value, '_blank', 'noopener,noreferrer')
  }
}
</script>

<template>
  <NuxtLink :to="clinicUrl" class="block group h-full">
    <UCard
      :class="['h-full transition-all hover:ring-1 hover:ring-primary hover:shadow-md', borderClass]"
      :ui="{ body: 'p-4 h-full' }"
    >
      <div :class="['flex flex-col h-full', { 'opacity-60': cardStatus === 'closed' }]">
        <!-- Top content: grows naturally -->
        <div class="space-y-3">
          <!-- Header Row: Badges -->
          <div class="flex items-start justify-between gap-2">
            <div class="flex items-center gap-1.5 flex-wrap">
              <EmergencyBadge
                v-if="clinic.isEmergency"
                :is24h="clinic.is24Hours"
                :is-on-call-only="clinic.isOnCallOnly"
                size="md"
              />
              <PremiumBadge v-if="clinic.isVerified" variant="verified" size="md" />
              <PremiumBadge v-if="clinic.isPremium" variant="premium" size="md" />
            </div>
            <div v-if="distance || showDistance" class="shrink-0">
              <UBadge color="primary" variant="subtle" size="xs">
                {{ distance }} km
              </UBadge>
            </div>
          </div>

          <!-- Title & Address (with optional thumbnail) -->
          <div class="flex gap-3">
            <img
              v-if="clinic.isPremium && clinic.imageUrl"
              :src="clinic.imageUrl"
              :alt="clinic.name"
              loading="lazy"
              class="w-16 h-16 rounded-lg object-cover shrink-0"
            >
            <div class="min-w-0">
              <h3 class="font-semibold text-highlighted group-hover:text-primary transition-colors line-clamp-2">
                {{ clinic.name }}
              </h3>
              <p class="text-base text-muted mt-1 truncate">
                {{ clinic.fullAddress || `${clinic.city.name}, ${clinic.city.county.name}` }}
              </p>
            </div>
          </div>

          <!-- Specialization Tags: Premium only -->
          <div
            v-if="clinic.isPremium && clinic.specializations && clinic.specializations.length > 0"
            class="flex items-center gap-1.5 flex-wrap"
          >
            <UBadge
              v-for="spec in clinic.specializations"
              :key="spec"
              color="primary"
              variant="subtle"
              size="sm"
            >
              {{ spec }}
            </UBadge>
          </div>

          <!-- Description Snippet: Premium only -->
          <p
            v-if="clinic.isPremium && clinic.about"
            class="text-base text-muted line-clamp-2"
          >
            {{ clinic.about }}
          </p>
        </div>

        <!-- Bottom content: anchored to card bottom -->
        <div class="mt-auto space-y-3 pt-3">
          <!-- Rating & Hours Row -->
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-2">
              <RatingStars
                v-if="clinic.rating"
                :rating="clinic.rating"
                size="md"
              />
              <span v-if="clinic.reviewCount" class="text-xs text-dimmed">
                ({{ clinic.reviewCount }})
              </span>
            </div>
            <OpenStatus
              v-if="clinic.workingHours || clinic.is24Hours"
              :hours="clinic.workingHours"
              :is24-hours="clinic.is24Hours"
              :emergency-hours="clinic.isEmergency ? clinic.emergencyHours : null"
              :is-on-call-only="clinic.isOnCallOnly"
              :compact="true"
            />
          </div>

          <!-- Quick Actions: Premium only -->
          <div
            v-if="clinic.isPremium && clinic.phone"
            class="pt-2 border-t border-default"
          >
            <div class="flex gap-2">
              <UButton
                color="primary"
                variant="solid"
                icon="i-lucide-phone"
                class="flex-1"
                @click="handleCall"
              >
                Zavolat
              </UButton>
              <UButton
                color="neutral"
                variant="outline"
                icon="i-lucide-navigation"
                class="flex-1"
                @click="handleDirections"
              >
                Navigovat
              </UButton>
            </div>
          </div>
        </div>
      </div>
    </UCard>
  </NuxtLink>
</template>
